import React from 'react';
import { Select } from 'antd';
import type { SelectProps } from 'antd';
import {
    FontSizeOutlined,
    AlignLeftOutlined,
    FieldNumberOutlined,
    UnorderedListOutlined,
    AppstoreAddOutlined,
    CheckCircleOutlined,
    CheckSquareOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    PictureOutlined,
    FunctionOutlined,
    FontColorsOutlined,
    LineOutlined,
} from '@ant-design/icons';
import { FieldType_V2 } from '../../../common/typescript/models/CollectFormV2';

export interface FieldTypeMeta {
    value: FieldType_V2;
    label: string;
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    borderColor: string;
}

export const FIELD_TYPE_META_LIST: FieldTypeMeta[] = [
    {
        value: FieldType_V2.TEXT,
        label: 'Văn bản',
        icon: <FontSizeOutlined />,
        color: '#2563eb',
        bgColor: '#eff6ff',
        borderColor: '#bfdbfe',
    },
    {
        value: FieldType_V2.TEXTAREA,
        label: 'Đoạn văn',
        icon: <AlignLeftOutlined />,
        color: '#3b82f6',
        bgColor: '#eff6ff',
        borderColor: '#bfdbfe',
    },
    {
        value: FieldType_V2.NUMBER,
        label: 'Số (Number)',
        icon: <FieldNumberOutlined />,
        color: '#059669',
        bgColor: '#ecfdf5',
        borderColor: '#a7f3d0',
    },
    {
        value: FieldType_V2.SELECT,
        label: 'Danh sách chọn',
        icon: <UnorderedListOutlined />,
        color: '#7c3aed',
        bgColor: '#f5f3ff',
        borderColor: '#ddd6fe',
    },
    {
        value: FieldType_V2.MULTISELECT,
        label: 'Chọn nhiều',
        icon: <AppstoreAddOutlined />,
        color: '#6d28d9',
        bgColor: '#f5f3ff',
        borderColor: '#ddd6fe',
    },
    {
        value: FieldType_V2.RADIO,
        label: 'Chọn một (Radio)',
        icon: <CheckCircleOutlined />,
        color: '#0891b2',
        bgColor: '#ecfeff',
        borderColor: '#a5f3fc',
    },
    {
        value: FieldType_V2.CHECKBOX,
        label: 'Hộp kiểm',
        icon: <CheckSquareOutlined />,
        color: '#0d9488',
        bgColor: '#f0fdfa',
        borderColor: '#99f6e4',
    },
    {
        value: FieldType_V2.DATE,
        label: 'Ngày tháng',
        icon: <CalendarOutlined />,
        color: '#d97706',
        bgColor: '#fffbeb',
        borderColor: '#fde68a',
    },
    {
        value: FieldType_V2.DATETIME,
        label: 'Ngày & Giờ',
        icon: <CalendarOutlined />,
        color: '#ea580c',
        bgColor: '#fff7ed',
        borderColor: '#fed7aa',
    },
    {
        value: FieldType_V2.TIME,
        label: 'Giờ (Time)',
        icon: <ClockCircleOutlined />,
        color: '#ca8a04',
        bgColor: '#fefce8',
        borderColor: '#fef08a',
    },
    {
        value: FieldType_V2.FILE,
        label: 'Tệp / Hình ảnh',
        icon: <PictureOutlined />,
        color: '#db2777',
        bgColor: '#fdf2f8',
        borderColor: '#fbcfe8',
    },
    {
        value: FieldType_V2.COMPUTED,
        label: 'Công thức',
        icon: <FunctionOutlined />,
        color: '#4f46e5',
        bgColor: '#eef2ff',
        borderColor: '#c7d2fe',
    },
    {
        value: FieldType_V2.HEADER,
        label: 'Tiêu đề nhóm',
        icon: <FontColorsOutlined />,
        color: '#475569',
        bgColor: '#f1f5f9',
        borderColor: '#cbd5e1',
    },
    {
        value: FieldType_V2.DIVIDER,
        label: 'Đường phân cách',
        icon: <LineOutlined />,
        color: '#64748b',
        bgColor: '#f8fafc',
        borderColor: '#e2e8f0',
    },
];

const FIELD_TYPE_META_MAP = new Map<string, FieldTypeMeta>(
    FIELD_TYPE_META_LIST.map((m) => [m.value, m]),
);

export const getFieldTypeMeta = (type?: FieldType_V2 | string): FieldTypeMeta => {
    if (type && FIELD_TYPE_META_MAP.has(type)) {
        return FIELD_TYPE_META_MAP.get(type)!;
    }
    return FIELD_TYPE_META_LIST[0];
};

export interface FieldTypeSelectProps extends Omit<SelectProps<FieldType_V2>, 'options'> {
    value?: FieldType_V2;
    onChange?: (value: FieldType_V2) => void;
}

export const FieldTypeSelect: React.FC<FieldTypeSelectProps> = ({
    value = FieldType_V2.TEXT,
    onChange,
    size = 'small',
    style,
    ...restProps
}) => {
    const options = FIELD_TYPE_META_LIST.map((meta) => ({
        value: meta.value,
        label: (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    lineHeight: 1.2,
                }}
            >
                <span
                    style={{
                        width: 20,
                        height: 20,
                        borderRadius: 5,
                        background: meta.bgColor,
                        border: `1px solid ${meta.borderColor}`,
                        color: meta.color,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        flexShrink: 0,
                    }}
                >
                    {meta.icon}
                </span>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#1e293b' }}>
                    {meta.label}
                </span>
            </span>
        ),
    }));

    return (
        <Select<FieldType_V2>
            size={size}
            value={value}
            onChange={onChange}
            options={options}
            popupMatchSelectWidth={210}
            style={{ width: '100%', ...style }}
            {...restProps}
        />
    );
};
